/**
 * Theme palette tokens + applyTheme helper.
 *
 * Split out of AppContext so fast-refresh can hot-reload the provider file
 * without having to re-evaluate these constants + DOM mutations on every
 * render of any component that subscribes to theme state.
 */
import type { AppTheme } from '../types';

export const THEME_PALETTE: Record<AppTheme, Record<string, string>> = {
  dark: {
    '--surface-0': '#0d0d0f',
    '--surface-1': '#111115',
    '--surface-2': '#17171d',
    '--surface-3': '#1e1e26',
    '--surface-4': '#26262f',
    '--surface-5': '#2e2e39',
    '--border-subtle': '#2a2a35',
    '--border': '#35354a',
    '--border-strong': '#4a4a66',
    '--ink-muted': '#6b6b88',
    '--ink-secondary': '#9494ae',
    '--ink-primary': '#d4d4e8',
    '--ink-bright': '#f0f0fa',
    '--accent': '#7c6af7',
    '--accent-hover': '#9182f9',
    '--accent-muted': '#3d3670',
  },
  light: {
    '--surface-0': '#f6f6fc',
    '--surface-1': '#eeeef8',
    '--surface-2': '#ffffff',
    '--surface-3': '#f2f2f9',
    '--surface-4': '#e8e8f4',
    '--surface-5': '#ddddef',
    '--border-subtle': '#e8e8f4',
    '--border': '#d4d4e8',
    '--border-strong': '#b8b8d0',
    '--ink-muted': '#9696b0',
    '--ink-secondary': '#5a5a7a',
    '--ink-primary': '#28283e',
    '--ink-bright': '#141428',
    '--accent': '#7c6af7',
    '--accent-hover': '#9182f9',
    '--accent-muted': '#3d3670',
  },
};

export function applyTheme(theme: AppTheme): void {
  const palette = THEME_PALETTE[theme] ?? THEME_PALETTE.dark;
  const root = document.documentElement;
  for (const [prop, value] of Object.entries(palette)) {
    root.style.setProperty(prop, value);
  }
  root.classList.toggle('dark', theme === 'dark');
  root.classList.toggle('light', theme === 'light');
}
